import { defineStore } from 'pinia'
import { computed, watch } from 'vue'
import { createSyncedRows, type RemoteSettings } from '../services/syncedRows'
import { useSettingsStore } from './settings'
import { writingDayKey } from '../utils/writingDays'
import {
  parseGoal, goalFromProject, goalFromWritingHabit, WRITING_HABIT_ID, type Goal,
} from '../utils/goalModel'

// Goals. Each syncs one row per goal (see syncedRows).
// Progress is never stored: it is read from the writing log, the word-count
// cache, and the goal log whenever it is shown.

export const useGoalsStore = defineStore('goals', () => {
  const settings = useSettingsStore()
  const goalRows = createSyncedRows<Goal>({
    storageKey: 'ink-goals',
    remotePrefix: 'goal.def.',
    parse: parseGoal,
  })
  // Project targets as they were stored before goals had measures. Read only
  // to move them into goals; see migrateProjects.
  const legacyProjectRows = createSyncedRows<any>({
    storageKey: 'ink-goal-projects',
    remotePrefix: 'goal.project.',
    parse: (raw) => (raw && typeof raw === 'object' ? raw : null),
  })
  /** Every goal in the writer's order; writing goals first when unordered. */
  const allGoals = computed(() =>
    Object.values(goalRows.values.value).sort((a, b) =>
      a.order - b.order
      || Number(b.measure === 'writing') - Number(a.measure === 'writing')
      || a.createdOn.localeCompare(b.createdOn)
      || a.id.localeCompare(b.id)))

  const goals = computed(() => allGoals.value.filter((goal) => !goal.archived))
  const archivedGoals = computed(() => allGoals.value.filter((goal) => goal.archived))

  /** Writing targets: the word counts a desk or folder should reach by a date. */
  const writingTargets = computed(() => goals.value
    .filter((goal) => goal.measure === 'writing' && goal.shape === 'target')
    .sort((a, b) => (a.deadline ?? '').localeCompare(b.deadline ?? '')))

  /** The writing habit shown in the bottom bar and on the calendar, if any. */
  const featuredHabit = computed(() => {
    const habits = goals.value.filter((goal) => goal.measure === 'writing' && goal.shape === 'habit')
    return habits.find((goal) => goal.featured) ?? habits[0] ?? null
  })

  function findGoal(id: string): Goal | null {
    return goalRows.values.value[id] ?? null
  }

  /** Saves a goal; featuring one writing habit unfeatures the others. Returns false if incomplete. */
  function saveGoal(goal: Goal): boolean {
    const parsed = parseGoal(goal)
    if (!parsed) return false
    if (parsed.featured) {
      for (const other of allGoals.value) {
        if (other.id !== parsed.id && other.featured) goalRows.write(other.id, { ...other, featured: false })
      }
    }
    goalRows.write(parsed.id, parsed)
    return true
  }

  function removeGoal(id: string) {
    if (goalRows.values.value[id]) goalRows.write(id, null)
  }

  /** Puts goals in the given order; goals not named keep their place after them. */
  function reorder(ids: string[]) {
    ids.forEach((id, index) => {
      const goal = goalRows.values.value[id]
      if (goal && goal.order !== index) goalRows.write(id, { ...goal, order: index })
    })
  }

  /** The next free position at the end of the list. */
  function nextOrder(): number {
    return allGoals.value.reduce((max, goal) => Math.max(max, goal.order + 1), 0)
  }

  // ── Moving the earlier, writing-only goals over ──────────────────────────
  // Both run whenever old data appears, including when it arrives from a
  // device not yet updated. Fixed ids keep two devices that migrate at once
  // from making duplicates.

  const today = () => writingDayKey(Date.now(), settings.goalDays.dayStartHour)

  function migrateWritingHabit() {
    const habit = settings.writingHabit
    if (!habit.enabled) return
    if (!goalRows.rows.value[WRITING_HABIT_ID]) {
      goalRows.write(WRITING_HABIT_ID, goalFromWritingHabit(habit, today()))
      settings.goalDays = { weekStartsOn: habit.weekStartsOn, dayStartHour: habit.dayStartHour }
    }
    settings.writingHabit = { ...habit, enabled: false }
  }

  function migrateProjects() {
    const legacy = Object.values(legacyProjectRows.values.value)
    if (!legacy.length) return
    let order = nextOrder()
    for (const raw of legacy) {
      if (!goalRows.rows.value[raw.id]) {
        const goal = goalFromProject(raw, order++)
        if (goal) goalRows.write(goal.id, goal)
      }
      legacyProjectRows.write(raw.id, null)
    }
  }

  watch(() => settings.writingHabit.enabled, migrateWritingHabit, { immediate: true })
  watch(() => legacyProjectRows.values.value, migrateProjects, { immediate: true })

  async function syncWithRemote(remote: RemoteSettings) {
    await Promise.all([
      goalRows.syncWithRemote(remote),
      legacyProjectRows.syncWithRemote(remote),
    ])
  }

  return {
    goals,
    archivedGoals,
    writingTargets,
    featuredHabit,
    findGoal,
    saveGoal,
    removeGoal,
    reorder,
    nextOrder,
    syncWithRemote,
  }
})
